import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { ProtokolSektion, ProtokolFeld } from '../types';
import protokollSchemaService from '../services/protokollSchemaService';
import { ankunft, markiereNeu } from '../utils/ankunft';
import { Icon, PageHeader, LoadingScreen, EmptyState, AnlegenButton, fehlerText } from '../components/ui';

const FELDTYPEN = [
  { value: 'radio_gruppe', label: 'Auswahl (Radio-Gruppe)', hint: 'z. B. Neuanlage / Sanierung / Erweiterung' },
  { value: 'ja_nein', label: 'Ja / Nein', hint: 'Zwei Optionen' },
  { value: 'ja_nein_ohne', label: 'Ja / Nein / Ohne', hint: 'Drei Optionen' },
  { value: 'text', label: 'Textfeld', hint: 'Freie Texteingabe' },
  { value: 'zahl', label: 'Zahlenfeld', hint: 'Zahl mit optionaler Einheit' },
  { value: 'freitext_liste', label: 'Freitext-Liste', hint: 'Beschriftete Textfelder (a, b, c ...)' },
];

const ProtokolKonfiguration: React.FC = () => {
  const [sektionen, setSektionen] = useState<ProtokolSektion[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedSektionId, setExpandedSektionId] = useState<number | null>(null);
  const [openSektionMenuId, setOpenSektionMenuId] = useState<number | null>(null);

  // Sektion-Modal
  const [showSektionModal, setShowSektionModal] = useState(false);
  const [editingSektion, setEditingSektion] = useState<ProtokolSektion | null>(null);
  const [sektionBuchstabe, setSektionBuchstabe] = useState('');
  const [sektionTitel, setSektionTitel] = useState('');
  const [sektionReihenfolge, setSektionReihenfolge] = useState(0);

  // Feld-Modal
  const [showFeldModal, setShowFeldModal] = useState(false);
  const [editingFeld, setEditingFeld] = useState<ProtokolFeld | null>(null);
  const [feldSektionId, setFeldSektionId] = useState<number | null>(null);
  const [feldBezeichnung, setFeldBezeichnung] = useState('');
  const [feldTyp, setFeldTyp] = useState('ja_nein');
  const [feldOptionen, setFeldOptionen] = useState('');
  const [feldEinheit, setFeldEinheit] = useState('');
  const [feldLabels, setFeldLabels] = useState('a,b,c,d,e,f');
  const [feldReihenfolge, setFeldReihenfolge] = useState(0);

  const sektionMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => { loadSchema(); }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (sektionMenuRef.current && !sektionMenuRef.current.contains(e.target as Node)) {
        setOpenSektionMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadSchema = async () => {
    try {
      setSektionen(await protokollSchemaService.getSchema());
    } catch {
      toast.error('Protokoll-Konfiguration konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  // ---- Sektion-Funktionen ----

  const openNewSektion = () => {
    setEditingSektion(null);
    setSektionBuchstabe('');
    setSektionTitel('');
    setSektionReihenfolge(sektionen.length + 1);
    setShowSektionModal(true);
  };

  const openEditSektion = (s: ProtokolSektion) => {
    setEditingSektion(s);
    setSektionBuchstabe(s.buchstabe);
    setSektionTitel(s.titel);
    setSektionReihenfolge(s.reihenfolge);
    setShowSektionModal(true);
  };

  const handleSaveSektion = async () => {
    if (!sektionBuchstabe.trim() || !sektionTitel.trim()) {
      toast.error('Buchstabe und Titel sind Pflichtfelder');
      return;
    }
    try {
      if (editingSektion) {
        await protokollSchemaService.updateSektion(editingSektion.id, {
          buchstabe: sektionBuchstabe.trim(),
          titel: sektionTitel.trim(),
          reihenfolge: sektionReihenfolge,
          aktiv: editingSektion.aktiv,
        });
        markiereNeu(`sektion-${editingSektion.id}`);
        toast.success('Sektion gespeichert');
      } else {
        const neu = await protokollSchemaService.createSektion({
          buchstabe: sektionBuchstabe.trim(),
          titel: sektionTitel.trim(),
          reihenfolge: sektionReihenfolge,
        });
        markiereNeu(`sektion-${neu.id}`);
        toast.success('Sektion angelegt');
      }
      setShowSektionModal(false);
      await loadSchema();
    } catch (err: any) {
      toast.error(fehlerText(err, 'Sektion konnte nicht gespeichert werden'));
    }
  };

  const handleDeleteSektion = async (s: ProtokolSektion) => {
    if (!window.confirm(`Sektion „${s.buchstabe} – ${s.titel}“ samt ihren Feldern löschen?`)) return;
    try {
      await protokollSchemaService.deleteSektion(s.id);
      toast.success('Sektion gelöscht');
      await loadSchema();
    } catch (err: any) {
      toast.error(fehlerText(err, 'Sektion konnte nicht gelöscht werden'));
    }
  };

  const handleToggleSektionAktiv = async (s: ProtokolSektion) => {
    try {
      await protokollSchemaService.updateSektion(s.id, {
        buchstabe: s.buchstabe,
        titel: s.titel,
        reihenfolge: s.reihenfolge,
        aktiv: !s.aktiv,
      });
      await loadSchema();
    } catch (err) {
      toast.error(fehlerText(err, 'Sektion konnte nicht gespeichert werden'));
    }
  };

  // ---- Feld-Funktionen ----

  const buildOptionenJson = (): string | null => {
    if (feldTyp === 'radio_gruppe') {
      const opts = feldOptionen.split(',').map(s => s.trim()).filter(Boolean);
      if (opts.length === 0) return null;
      return JSON.stringify({ optionen: opts });
    }
    if (feldTyp === 'zahl') {
      const obj: any = {};
      if (feldEinheit.trim()) obj.einheit = feldEinheit.trim();
      return Object.keys(obj).length > 0 ? JSON.stringify(obj) : null;
    }
    if (feldTyp === 'freitext_liste') {
      const labels = feldLabels.split(',').map(s => s.trim()).filter(Boolean);
      return JSON.stringify({ labels });
    }
    return null;
  };

  const loadOptionenJson = (json: string | null, typ: string) => {
    if (!json) return;
    try {
      const obj = JSON.parse(json);
      if (typ === 'radio_gruppe' && obj.optionen) setFeldOptionen(obj.optionen.join(', '));
      if (typ === 'zahl' && obj.einheit) setFeldEinheit(obj.einheit);
      if (typ === 'freitext_liste' && obj.labels) setFeldLabels(obj.labels.join(', '));
    } catch {}
  };

  const openNewFeld = (sektionId: number) => {
    setEditingFeld(null);
    setFeldSektionId(sektionId);
    setFeldBezeichnung('');
    setFeldTyp('ja_nein');
    setFeldOptionen('');
    setFeldEinheit('');
    setFeldLabels('a, b, c, d, e, f');
    const sektion = sektionen.find(s => s.id === sektionId);
    setFeldReihenfolge((sektion?.felder.length || 0) + 1);
    setShowFeldModal(true);
  };

  const openEditFeld = (feld: ProtokolFeld) => {
    setEditingFeld(feld);
    setFeldSektionId(feld.sektionId);
    setFeldBezeichnung(feld.bezeichnung);
    setFeldTyp(feld.feldtyp);
    setFeldOptionen('');
    setFeldEinheit('');
    setFeldLabels('a, b, c, d, e, f');
    loadOptionenJson(feld.optionenJson, feld.feldtyp);
    setFeldReihenfolge(feld.reihenfolge);
    setShowFeldModal(true);
  };

  const handleSaveFeld = async () => {
    if (!feldBezeichnung.trim()) { toast.error('Bezeichnung ist ein Pflichtfeld'); return; }
    if (!feldSektionId) return;
    const optionenJson = buildOptionenJson();
    try {
      if (editingFeld) {
        await protokollSchemaService.updateFeld(editingFeld.id, {
          bezeichnung: feldBezeichnung.trim(),
          feldtyp: feldTyp,
          optionenJson,
          reihenfolge: feldReihenfolge,
          aktiv: editingFeld.aktiv,
        });
        markiereNeu(`feld-${editingFeld.id}`);
        toast.success('Feld gespeichert');
      } else {
        const neu = await protokollSchemaService.createFeld({
          sektionId: feldSektionId,
          feldtyp: feldTyp,
          bezeichnung: feldBezeichnung.trim(),
          optionenJson,
          reihenfolge: feldReihenfolge,
        });
        markiereNeu(`feld-${neu.id}`);
        toast.success('Feld angelegt');
      }
      setShowFeldModal(false);
      await loadSchema();
    } catch (err: any) {
      toast.error(fehlerText(err, 'Feld konnte nicht gespeichert werden'));
    }
  };

  const handleDeleteFeld = async (feld: ProtokolFeld) => {
    if (!window.confirm(`Feld „${feld.bezeichnung}“ löschen?`)) return;
    try {
      await protokollSchemaService.deleteFeld(feld.id);
      toast.success('Feld gelöscht');
      await loadSchema();
    } catch (err: any) {
      toast.error(fehlerText(err, 'Feld konnte nicht gelöscht werden'));
    }
  };

  const handleToggleFeldAktiv = async (feld: ProtokolFeld) => {
    try {
      await protokollSchemaService.updateFeld(feld.id, {
        bezeichnung: feld.bezeichnung,
        feldtyp: feld.feldtyp,
        optionenJson: feld.optionenJson,
        reihenfolge: feld.reihenfolge,
        aktiv: !feld.aktiv,
      });
      await loadSchema();
    } catch (err) {
      toast.error(fehlerText(err, 'Feld konnte nicht gespeichert werden'));
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <PageHeader
        title="Protokoll-Konfiguration"
        subtitle="Sektionen und Felder des Inbetriebnahmeprotokolls verwalten"
        actions={<AnlegenButton onClick={openNewSektion}>Sektion anlegen</AnlegenButton>}
      />

      {sektionen.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200">
          <EmptyState icon={<Icon.Protokoll className="h-6 w-6" />} title="Noch keine Sektionen"
            hint="Sektionen gliedern das Inbetriebnahmeprotokoll, darin liegen die Felder."
            action={<AnlegenButton onClick={openNewSektion}>Sektion anlegen</AnlegenButton>} />
        </div>
      )}

      <div className="space-y-4">
        {sektionen.map(sektion => {
          const a = ankunft(`sektion-${sektion.id}`);
          return (
          <div key={sektion.id} ref={a.ref} className={`bg-white rounded-xl border ${sektion.aktiv ? 'border-gray-200' : 'border-gray-100 opacity-60'} ${a.className}`}>
            {/* Sektions-Header */}
            <div className="flex items-center px-4 py-3">
              {/* Klickbarer Bereich zum Aufklappen */}
              <div
                className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                onClick={() => setExpandedSektionId(expandedSektionId === sektion.id ? null : sektion.id)}
              >
                <span className="w-7 h-7 flex-shrink-0 rounded-full bg-brand-700 text-white text-xs font-bold flex items-center justify-center">
                  {sektion.buchstabe}
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-800 truncate">{sektion.titel}</span>
                    <span className="text-xs text-gray-400 flex-shrink-0">{sektion.felder.length} Felder</span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`text-xs px-1.5 py-0.5 rounded ${sektion.aktiv ? 'text-green-700 bg-green-50' : 'text-gray-400 bg-gray-100'}`}>
                      {sektion.aktiv ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Drei-Punkte-Menü */}
              <div className="relative flex-shrink-0 ml-2" ref={openSektionMenuId === sektion.id ? sektionMenuRef : undefined}>
                <button
                  onClick={e => { e.stopPropagation(); setOpenSektionMenuId(openSektionMenuId === sektion.id ? null : sektion.id); }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Weitere Aktionen"
                >
                  <Icon.Mehr className="w-5 h-5" />
                </button>
                {openSektionMenuId === sektion.id && (
                  <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-lg shadow-lg border border-gray-200 z-30 py-1">
                    <button
                      onClick={e => { e.stopPropagation(); setOpenSektionMenuId(null); handleToggleSektionAktiv(sektion); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      {sektion.aktiv ? 'Deaktivieren' : 'Aktivieren'}
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); setOpenSektionMenuId(null); openEditSektion(sektion); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Bearbeiten
                    </button>
                    <div className="border-t border-gray-100 my-1" />
                    <button
                      onClick={e => { e.stopPropagation(); setOpenSektionMenuId(null); handleDeleteSektion(sektion); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                    >
                      Löschen
                    </button>
                  </div>
                )}
              </div>

              {/* Expand-Chevron */}
              <button
                onClick={() => setExpandedSektionId(expandedSektionId === sektion.id ? null : sektion.id)}
                className="flex-shrink-0 p-1 text-gray-400 ml-1"
              >
                <Icon.ChevronDown className={`w-4 h-4 transition-transform ${expandedSektionId === sektion.id ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {/* Felder */}
            {expandedSektionId === sektion.id && (
              <div className="ui-aufklappen border-t border-gray-100">
                <div className="divide-y divide-gray-50">
                  {sektion.felder.map(feld => {
                    const af = ankunft(`feld-${feld.id}`);
                    return (
                    <div key={feld.id} ref={af.ref} className={`flex items-start justify-between px-4 py-3 ${!feld.aktiv ? 'opacity-50' : ''} ${af.className}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-700 truncate">{feld.bezeichnung}</span>
                          <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded flex-shrink-0">
                            {FELDTYPEN.find(t => t.value === feld.feldtyp)?.label || feld.feldtyp}
                          </span>
                          {!feld.aktiv && <span className="text-xs text-gray-400 italic">inaktiv</span>}
                        </div>
                        {feld.optionenJson && (
                          <div className="text-xs text-gray-400 mt-0.5 truncate">{feld.optionenJson}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                        <button
                          onClick={() => handleToggleFeldAktiv(feld)}
                          title={feld.aktiv ? 'Deaktivieren' : 'Aktivieren'}
                          className={`p-1.5 rounded ${feld.aktiv ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`}
                        >
                          {feld.aktiv
                            ? <Icon.Check className="w-4 h-4" />
                            : <Icon.Plus className="w-4 h-4" />
                          }
                        </button>
                        <button
                          onClick={() => openEditFeld(feld)}
                          title="Bearbeiten"
                          className="p-1.5 text-gray-500 hover:text-gray-700 rounded hover:bg-gray-100"
                        >
                          <Icon.Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteFeld(feld)}
                          title="Löschen"
                          className="p-1.5 text-red-400 hover:text-red-600 rounded hover:bg-red-50"
                        >
                          <Icon.Trash className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    );
                  })}
                </div>
                <div className="px-4 py-3 border-t border-gray-50">
                  <button
                    onClick={() => openNewFeld(sektion.id)}
                    className="inline-flex items-center gap-1 text-sm text-brand-600 hover:text-brand-800 font-medium"
                  >
                    <Icon.Plus className="w-4 h-4" />Feld anlegen
                  </button>
                </div>
              </div>
            )}
          </div>
          );
        })}
      </div>

      {/* Sektion-Modal */}
      {showSektionModal && (
        <div className="ui-fade fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="ui-pop bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingSektion ? 'Sektion bearbeiten' : 'Neue Sektion'}
              </h2>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="flex gap-4">
                <div className="w-24">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Buchstabe *</label>
                  <input
                    type="text"
                    maxLength={5}
                    value={sektionBuchstabe}
                    onChange={e => setSektionBuchstabe(e.target.value.toUpperCase())}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                    placeholder="A"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reihenfolge</label>
                  <input
                    type="number"
                    value={sektionReihenfolge}
                    onChange={e => setSektionReihenfolge(Number(e.target.value))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titel *</label>
                <input
                  type="text"
                  value={sektionTitel}
                  onChange={e => setSektionTitel(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  placeholder="z. B. Filteranlagen"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3 justify-end">
              <button onClick={() => setShowSektionModal(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50">Abbrechen</button>
              <button onClick={handleSaveSektion} className="px-4 py-2 bg-brand-700 text-white rounded-lg text-sm hover:bg-brand-800">{editingSektion ? 'Speichern' : 'Sektion anlegen'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Feld-Modal */}
      {showFeldModal && (
        <div className="ui-fade fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="ui-pop bg-white rounded-2xl shadow-xl w-full max-w-lg">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingFeld ? 'Feld bearbeiten' : 'Neues Feld'}
              </h2>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bezeichnung *</label>
                <input
                  type="text"
                  value={feldBezeichnung}
                  onChange={e => setFeldBezeichnung(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  placeholder="z. B. Heizfunktion geprüft"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Feldtyp *</label>
                <select
                  value={feldTyp}
                  onChange={e => { setFeldTyp(e.target.value); setFeldOptionen(''); setFeldEinheit(''); }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  {FELDTYPEN.map(t => (
                    <option key={t.value} value={t.value}>{t.label} – {t.hint}</option>
                  ))}
                </select>
              </div>

              {feldTyp === 'radio_gruppe' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Optionen (kommagetrennt) *</label>
                  <input
                    type="text"
                    value={feldOptionen}
                    onChange={e => setFeldOptionen(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                    placeholder="Neuanlage, Sanierung, Erweiterung"
                  />
                </div>
              )}

              {feldTyp === 'zahl' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Einheit (optional)</label>
                  <input
                    type="text"
                    value={feldEinheit}
                    onChange={e => setFeldEinheit(e.target.value)}
                    className="w-40 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                    placeholder="z. B. °C, bar, m³"
                  />
                </div>
              )}

              {feldTyp === 'freitext_liste' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Zeilenbeschriftungen (kommagetrennt)</label>
                  <input
                    type="text"
                    value={feldLabels}
                    onChange={e => setFeldLabels(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                    placeholder="a, b, c, d, e, f"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reihenfolge</label>
                <input
                  type="number"
                  value={feldReihenfolge}
                  onChange={e => setFeldReihenfolge(Number(e.target.value))}
                  className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3 justify-end">
              <button onClick={() => setShowFeldModal(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50">Abbrechen</button>
              <button onClick={handleSaveFeld} className="px-4 py-2 bg-brand-700 text-white rounded-lg text-sm hover:bg-brand-800">{editingFeld ? 'Speichern' : 'Feld anlegen'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProtokolKonfiguration;
